import { Component, Input, OnInit } from '@angular/core';
import { ElementType } from 'src/app/_objects/element-type';
import { ReportSection } from 'src/app/_objects/report-section';
import { FromToDetail } from 'src/app/_objects/report-template';
import { ReportUnits } from 'src/app/_objects/report-units';
import { MousePoint, Notifier, ReportTemplateDesignService } from 'src/app/_services/report-template-design.service';

@Component({
    selector: 'app-report-section',
    templateUrl: './report-section.component.html',
    styleUrls: ['./report-section.component.scss'],
    standalone: false
})
export class ReportSectionComponent implements OnInit {
  @Input() public pageKey: string;
  @Input() public sectionKey: string;
  @Input() public elementLocation: string;
  public reportSection: ReportSection;
  private notifier: Notifier;
  private resizeMarkerSize: number;
  private resizing: boolean;
  public isCustomDrawEnable: boolean;

  constructor(public designService: ReportTemplateDesignService) {
    this.resizeMarkerSize = 36;
    this.resizing = false;
  }

  public ngOnInit() {
    this.notifier = {
      componentName: 'report-section.component',
      ids: { pageId: this.pageKey, elementId: this.sectionKey }
    };
    this.isCustomDrawEnable = this.designService.customElementAdd;
    this.reportSection = this.designService.getSection(this.sectionKey);
    let elementNavigator = this.designService.navigatorElements
    .filter((x) => x.pageKey == this.pageKey)[0]
    .elementsDetails.filter((y) => y.location == this.elementLocation)[0]
      .elements;

     elementNavigator.filter(x=>x.elementName == ElementType.Section).length > 0
      ? elementNavigator.filter(x=>x.elementName == ElementType.Section)[0].elementData.push({
          id: this.sectionKey,
          page: this.pageKey,
          name: this.reportSection.getTitle(),
        })
      : elementNavigator.push({
          elementName: ElementType.Section,
          isVisible: false,
          elementData: [
            {
              id: this.sectionKey,
              page: this.pageKey,
              name: this.reportSection.getTitle(),
            },
          ],
        });
  }

  public onClick(event: MouseEvent) {
    if (this.reportSection.isSelectable) {
      this.designService.notifySelectElement(this.notifier, this.sectionKey, event.ctrlKey);
      event.stopPropagation();
    }
  }

  public getResizeMarkerSize(): string {
    return this.resizeMarkerSize + 'px';
  }

  public getResizeMarkerLeft(horizontal: string): string {
    switch (horizontal) {
      case 'left':
        return (this.resizeMarkerSize / 2 * -1) + 'px';
      case 'center':
        return ((this.reportSection.getWidthPx() / 2) - (this.resizeMarkerSize / 2)) + 'px';
      case 'right':
        return (this.reportSection.getWidthPx() - (this.resizeMarkerSize / 2)) + 'px';
      default:
        return '';
    }
  }

  public getResizeMarkerTop(vertical: string): string {
    switch (vertical) {
      case 'top':
        return (this.resizeMarkerSize / 2 * -1) + 'px';
      case 'middle':
        return ((this.reportSection.getHeightPx() / 2) - (this.resizeMarkerSize / 2)) + 'px';
      case 'bottom':
        return (this.reportSection.getHeightPx() - (this.resizeMarkerSize / 2)) + 'px';
      default:
        return '';
    }
  }

  public onDragStart(event: DragEvent) {
    event.dataTransfer.setData('text', this.sectionKey);
    event.dataTransfer.effectAllowed = 'move';

    const fromDetail: FromToDetail = {
      location: this.reportSection.getLocation(),
      pageId: this.pageKey
    };
    const dragPoint: MousePoint = {
      onElementX: event.offsetX,
      onElementY: event.offsetY
    };

    this.designService.notifyBeginDrag(this.notifier, ElementType.Signature, fromDetail, dragPoint);
  }

  public onDragOver(event: DragEvent) {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    event.stopPropagation();
  }

  public onDrop(event: DragEvent) {
    event.preventDefault();
    const template  = {...this.designService.originTemplate};
    template.reportTemplateContent = JSON.stringify(this.designService.getTemplate());
    this.designService.templateForUndo.push(template);
    const dragElemId = event.dataTransfer.getData('text');

    const toDetail: FromToDetail = {
      location: this.reportSection.getLocation(),
      pageId: this.pageKey
    };
    const dropPoint: MousePoint = {
      onElementX: this.reportSection.getLeftPx() + event.offsetX,
      onElementY: this.reportSection.getTopPx() + event.offsetY
    };

    this.designService.notifyDrop(this.notifier, dragElemId, toDetail, dropPoint);
    event.dataTransfer.clearData('text');
    event.stopPropagation();
  }

  public onResizeMarkerDown(event: MouseEvent, vertical: string, horizontal: string) {
    event.preventDefault();
    this.resizing = true;
    const beginPoint: MousePoint = {
      onElementX: event.offsetX,
      onElementY: event.offsetY
    };
    this.designService.notifyBeginResize(this.notifier, ElementType.Text, horizontal, vertical, beginPoint);
    event.stopPropagation();
  }

  public onResizing(event: MouseEvent) {
    event.preventDefault();
    if (this.resizing) {
      this.designService.notifyResize(this.notifier, { onElementX: event.offsetX, onElementY: event.offsetY });
      this.designService.updateStageTagForSection(this.sectionKey);
    }
    event.stopPropagation();
  }

  public onResizeMarkerUp(event: MouseEvent) {
    event.preventDefault();
    this.resizing = false;
    event.stopPropagation();
  }

  public onResizeMarkerLeave(event: MouseEvent) {
    event.preventDefault();
    this.resizing = false;
    event.stopPropagation();
  }

  public getStyle(): object {
    return {
      top: this.reportSection.top,
      left: this.reportSection.left,
      width: this.reportSection.width,
      height: this.reportSection.height,
      position: 'absolute',
      border: '1px dashed #333'
    };
  }
}
